import React, { useEffect, useRef } from 'react';
import { ChevronDownIcon, ClockIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { SmsConversation } from '../../types/sms';
import {
  SMS_INBOX_PIPELINE_STATUSES,
  formatLeadStatusLabel,
  leadStatusPillClass,
} from '../../constants/pipelineStatuses';
import { LeadStatus } from '../../types/lead';
import { formatFollowUpDate } from '../Leads/FollowUpDateEditor';
import LoadingSpinner from '../UI/LoadingSpinner';

interface SmsInboxSidebarProps {
  conversations: SmsConversation[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  totalUnread: number;
  selectedId: string | null;
  search: string;
  filterTab: 'all' | 'unread' | 'starred' | LeadStatus;
  followUpDateFrom: string;
  followUpDateTo: string;
  statusDropOpen: boolean;
  onSearchChange: (value: string) => void;
  onFilterTabChange: (tab: 'all' | 'unread' | 'starred' | LeadStatus) => void;
  onFollowUpDateFromChange: (value: string) => void;
  onFollowUpDateToChange: (value: string) => void;
  onStatusDropToggle: () => void;
  onStatusDropClose: () => void;
  onSelect: (id: string) => void;
  onToggleStar: (id: string, starred: boolean) => void;
  onLoadMore: () => void;
  statusDropRef: React.RefObject<HTMLDivElement>;
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

const SmsInboxSidebar: React.FC<SmsInboxSidebarProps> = ({
  conversations,
  loading,
  loadingMore,
  hasMore,
  totalUnread,
  selectedId,
  search,
  filterTab,
  followUpDateFrom,
  followUpDateTo,
  statusDropOpen,
  onSearchChange,
  onFilterTabChange,
  onFollowUpDateFromChange,
  onFollowUpDateToChange,
  onStatusDropToggle,
  onStatusDropClose,
  onSelect,
  onToggleStar,
  onLoadMore,
  statusDropRef,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);

  const isPipelineFilter = filterTab !== 'all' && filterTab !== 'unread' && filterTab !== 'starred';

  const statusLabel = isPipelineFilter ? formatLeadStatusLabel(filterTab) : 'Status';

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    const root = listRef.current;
    if (!sentinel || !root || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loadingMore) {
          onLoadMore();
        }
      },
      { root, rootMargin: '120px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, onLoadMore, conversations.length]);

  return (
    <div className="flex flex-col bg-card/80 backdrop-blur-xl border border-border shadow-lg overflow-hidden w-full sm:w-[300px] lg:w-[320px] xl:w-[360px] shrink-0 rounded-[20px] lg:rounded-[28px]">
      <div className="px-4 pt-4 pb-3">
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search merchant, business, phone..."
          className="w-full bg-muted/80 border border-border rounded-2xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground outline-none focus:ring-2 focus:ring-ring transition"
        />
        {search.trim().length > 0 && search.trim().length < 2 && (
          <p className="text-[10px] text-muted-foreground mt-1.5 px-1">Type at least 2 characters to search</p>
        )}
      </div>

      <div className="px-4 pb-3 flex flex-wrap gap-2 items-center">
        <button
          type="button"
          onClick={() => onFilterTabChange('all')}
          className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
            filterTab === 'all'
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          All
        </button>
        <button
          type="button"
          onClick={() => onFilterTabChange('unread')}
          className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
            filterTab === 'unread'
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          Unread
          {totalUnread > 0 && (
            <span className="text-[10px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 bg-primary text-primary-foreground">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => onFilterTabChange('starred')}
          className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
            filterTab === 'starred'
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          <StarIcon className="h-3.5 w-3.5 shrink-0" />
          Starred
        </button>

        <div className="relative" ref={statusDropRef}>
          <button
            type="button"
            onClick={onStatusDropToggle}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
              isPipelineFilter
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            <span>{statusLabel}</span>
            <ChevronDownIcon
              className={`h-3.5 w-3.5 shrink-0 opacity-80 transition-transform ${
                statusDropOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
          {statusDropOpen && (
            <div className="absolute top-full left-0 mt-1.5 bg-popover border border-border rounded-2xl shadow-xl p-1.5 w-44 z-20 max-h-64 overflow-y-auto">
              {SMS_INBOX_PIPELINE_STATUSES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => {
                    onFilterTabChange(s.value);
                    onStatusDropClose();
                  }}
                  className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${
                    filterTab === s.value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {filterTab === 'follow_up' && (
          <div className="w-full flex flex-wrap gap-2 items-center bg-muted rounded-2xl p-2 mt-1">
            <input
              type="date"
              value={followUpDateFrom}
              onChange={(e) => onFollowUpDateFromChange(e.target.value)}
              className="bg-card border border-border rounded-xl px-2 py-1.5 text-xs outline-none"
            />
            <input
              type="date"
              value={followUpDateTo}
              onChange={(e) => onFollowUpDateToChange(e.target.value)}
              className="bg-card border border-border rounded-xl px-2 py-1.5 text-xs outline-none"
            />
          </div>
        )}
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto px-3 pb-3 space-y-2 scroll-area">
        {loading && (
          <div className="text-sm text-muted-foreground text-center py-10">Loading…</div>
        )}
        {!loading && conversations.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-10 px-4">No conversations found.</div>
        )}
        {conversations.map((row) => {
          const active = row.id === selectedId;
          return (
            <button
              key={row.id}
              type="button"
              onClick={() => onSelect(row.id)}
              className={`w-full text-left rounded-3xl p-4 transition-all ${
                active
                  ? 'bg-primary/10 border border-primary/30 shadow-sm scale-[1.01]'
                  : 'bg-card/70 hover:bg-card border border-transparent hover:scale-[1.01]'
              }`}
            >
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className="text-base font-semibold truncate text-foreground flex-1 min-w-0">
                      {row.lead_name || row.lead_phone || 'Unknown'}
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleStar(row.id, !row.is_starred);
                      }}
                      className="shrink-0 p-0.5 rounded-md hover:bg-muted transition-colors"
                      title={row.is_starred ? 'Remove star' : 'Star conversation'}
                      aria-label={row.is_starred ? 'Remove star' : 'Star conversation'}
                    >
                      {row.is_starred ? (
                        <StarIconSolid className="h-4 w-4 text-amber-500" />
                      ) : (
                        <StarIcon className="h-4 w-4 text-muted-foreground/60 hover:text-amber-500" />
                      )}
                    </button>
                  </div>
                  {row.company_name && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{row.company_name}</p>
                  )}
                  {row.follow_up_date && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1 truncate">
                      <ClockIcon className="h-3 w-3 shrink-0" />
                      <span className="truncate">{formatFollowUpDate(row.follow_up_date, true)}</span>
                    </p>
                  )}
                </div>
                <span
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${leadStatusPillClass(row.lead_status)}`}
                >
                  {formatLeadStatusLabel(row.lead_status)}
                </span>
              </div>
              {row.last_message_preview && (
                <p className="text-sm text-muted-foreground mt-2 truncate">{row.last_message_preview}</p>
              )}
              <div className="flex justify-between items-center mt-2">
                {row.last_message_at && (
                  <span className="text-[10px] text-muted-foreground">{formatTime(row.last_message_at)}</span>
                )}
                {row.unread_count > 0 && (
                  <span className="bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {row.unread_count > 9 ? '9+' : row.unread_count}
                  </span>
                )}
              </div>
            </button>
          );
        })}

        {!loading && hasMore && (
          <div ref={loadMoreSentinelRef} className="flex justify-center py-3">
            {loadingMore ? (
              <LoadingSpinner size="sm" />
            ) : (
              <span className="text-[11px] text-muted-foreground">Scroll for more</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SmsInboxSidebar;
